// src/components/ProfileCompletionBanner.tsx
//
// Profile-completion banner shown at the top of the Dashboard after signup.
// Disappears once every required field is filled.
//
// Backed by GET /auth/onboarding/status which derives completion state from
// the live User row (so it stays in sync when fields are filled directly).

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";

type ChecklistItem = {
  key: string;
  label: string;
  complete: boolean;
  actionLink: string;
};

type Status = {
  profileComplete: boolean;
  checklist: ChecklistItem[];
};

const ProfileCompletionBanner: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get("/auth/onboarding/status")
      .then((r) => {
        if (!cancelled) setStatus(r.data);
      })
      .catch(() => {
        /* non-fatal */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Hide entirely if user has finished or if we have no data yet
  if (!status || status.profileComplete || dismissed) return null;

  const completedCount = status.checklist.filter((c) => c.complete).length;
  const totalCount = status.checklist.length;
  const percent = Math.round((completedCount / totalCount) * 100);

  return (
    <div
      role="status"
      aria-label="Profile completion"
      style={{
        background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
        border: "1px solid #93c5fd",
        borderRadius: 12,
        padding: 16,
        margin: "0 0 20px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 0 }}>
          <div
            style={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: "50%",
              background: "#1d4ed8",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 13,
              fontWeight: 700,
            }}
            aria-hidden="true"
          >
            {percent}%
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: "#1e3a8a", fontSize: 15 }}>
              Complete your profile to unlock better job matches
            </div>
            <div style={{ fontSize: 13, color: "#1e40af", marginTop: 2 }}>
              {completedCount} of {totalCount} done · {totalCount - completedCount} remaining
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button
            onClick={() => setExpanded((e) => !e)}
            style={{
              background: "transparent",
              border: "1px solid #1d4ed8",
              color: "#1d4ed8",
              borderRadius: 6,
              padding: "6px 12px",
              fontWeight: 600,
              cursor: "pointer",
              fontSize: 13,
            }}
            aria-expanded={expanded}
          >
            {expanded ? "Hide" : "Show"} steps
          </button>
          <button
            onClick={() => setDismissed(true)}
            title="Hide for this session"
            aria-label="Hide for this session"
            style={{
              background: "transparent",
              border: "none",
              color: "#1e40af",
              fontSize: 20,
              lineHeight: 1,
              cursor: "pointer",
              padding: "0 4px",
            }}
          >
            ×
          </button>
        </div>
      </div>

      {expanded && (
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: "12px 0 0",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 8,
          }}
        >
          {status.checklist.map((item) => (
            <li
              key={item.key}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: item.complete ? "#dcfce7" : "#fff",
                border: `1px solid ${item.complete ? "#86efac" : "#cbd5e1"}`,
                borderRadius: 8,
                padding: "8px 12px",
                gap: 8,
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: item.complete ? "#166534" : "#334155" }}>
                <span aria-hidden="true" style={{ fontSize: 14 }}>
                  {item.complete ? "✓" : "○"}
                </span>
                <span style={{ textDecoration: item.complete ? "line-through" : "none" }}>
                  {item.label}
                </span>
              </span>
              {!item.complete && (
                <button
                  onClick={() => navigate(item.actionLink)}
                  style={{
                    background: "#1d4ed8",
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    padding: "4px 10px",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  Complete →
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ProfileCompletionBanner;
